import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState, useEffect } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-location';
import SpinnerIcon from './icon/SpinnerIcon';
import { apiRawFetch } from '../client';

const ResetPassword = () => {
    const search = useSearch();
    const navigate = useNavigate();
    const token = search.token as string | undefined;

    const [tokenValid, setTokenValid] = useState<boolean | null>(null);
    const [maskedEmail, setMaskedEmail] = useState<string>('');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isVerifying, setIsVerifying] = useState(true);
    const [isSuccess, setIsSuccess] = useState(false);

    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setTokenValid(false);
                setIsVerifying(false);
                return;
            }

            try {
                const response = await apiRawFetch(`api/auth/reset-password/verify/${token}`, 'GET');

                if (response.ok) {
                    const data = await response.json();
                    if (data.valid) {
                        setTokenValid(true);
                        setMaskedEmail(data.email || '');
                    } else {
                        setTokenValid(false);
                    }
                } else {
                    setTokenValid(false);
                }
            } catch (err) {
                setTokenValid(false);
            } finally {
                setIsVerifying(false);
            }
        };

        verifyToken();
    }, [token]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        // Validate password
        if (password.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (!token) {
            setError('Invalid reset token');
            return;
        }

        setIsLoading(true);

        try {
            const response = await apiRawFetch('api/auth/reset-password', 'POST', {
                token,
                password,
            });

            if (response.ok) {
                setIsSuccess(true);
            } else {
                const data = await response.json();
                setError(data.message || 'Failed to reset password. The link may have expired.');
            }
        } catch (err) {
            setError(`Failed to reset password. Please try again. ${err}`);
        } finally {
            setIsLoading(false);
        }
    };

    if (isVerifying) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Box display="flex" justifyContent="center">
                    <SpinnerIcon />
                </Box>
            </Container>
        );
    }

    if (isSuccess) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Card>
                    <CardContent>
                        <Typography variant="h5" component="h1" gutterBottom>
                            Password Reset Successful
                        </Typography>
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Your password has been successfully reset.
                        </Alert>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            You can now log in with your new password.
                        </Typography>
                        <Button variant="contained" onClick={() => navigate({ to: '/' })}>
                            Go to Login
                        </Button>
                    </CardContent>
                </Card>
            </Container>
        );
    }

    if (!tokenValid) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Card>
                    <CardContent>
                        <Typography variant="h5" component="h1" gutterBottom>
                            Invalid Reset Link
                        </Typography>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            This password reset link is invalid, has expired, or has already been used.
                        </Alert>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Password reset links expire after 1 hour and can only be used once.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button variant="contained" onClick={() => navigate({ to: '/forgot-password' })}>
                                Request New Link
                            </Button>
                            <Button variant="outlined" onClick={() => navigate({ to: '/' })}>
                                Back to Login
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Card>
                <CardContent>
                    <Typography variant="h5" component="h1" gutterBottom>
                        Reset Your Password
                    </Typography>
                    <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                        Enter a new password for your account ({maskedEmail}).
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={2}>
                        <TextField
                            label="New Password"
                            type="password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            required
                            disabled={isLoading}
                            fullWidth
                            helperText="Minimum 8 characters"
                            autoFocus
                        />
                        <TextField
                            label="Confirm New Password"
                            type="password"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            required
                            disabled={isLoading}
                            fullWidth
                        />
                        {error && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                                {error}
                            </Alert>
                        )}
                        <Button type="submit" variant="contained" disabled={isLoading} sx={{ mt: 2 }}>
                            {isLoading ? 'Resetting Password...' : 'Reset Password'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Container>
    );
};

export default ResetPassword;
